// client/src/lib/api/reports-api.ts
// ===========================================
// MÓDULO DE REPORTES Y DASHBOARD
// Maneja estadísticas de dashboard, reportes de ventas, productos, clientes,
// discrepancias, transferencias y reportes consolidados de owner.
// Usa apiRequest para inyectar headers y manejar errores de forma tipada.
// ===========================================

import { apiRequest, buildUrlWithParams } from "./api-client";

// -------------------------------------------
// Dashboard de tienda (con filtros de fecha)
// -------------------------------------------
export async function getShopDashboardStats(
  shopId: string,
  filters?: { startDate?: string; endDate?: string },
): Promise<{
  // ✅ CORREGIDO: Se restauró la clave 'data:' que faltaba
  data: {
    summary: {
      totalRevenue: number;
      totalSales: number;
      averageTicket: number;
      todayRevenue: number;
      todaySales: number;
      todayAverageTicket: number;
      totalProducts: number;
      lowStockItems: number;
      uniqueCustomersToday: number;
      activeCustomers: number;
    };
    paymentMethods: { efectivo: number; transferencia: number; mixto: number };
    hourlySales?: Array<{ hour: number; salesCount: number; revenue: number }>;
    topProducts: Array<{
      productId: string;
      productName: string;
      totalQuantity: number;
      totalRevenue: number;
    }>;
    recentSales: Array<{
      saleId: string;
      total: number;
      paymentMethod: string;
      createdAt: Date | string;
      customerName?: string;
      productName?: string;
      productCount?: number;
    }>;
    recentCustomers: Array<{
      id: string;
      fullName: string;
      phone: string;
      totalPurchases: number;
      totalSpent: number;
      lastPurchaseAt?: string;
      daysSinceLastPurchase: number;
    }>;
  };
  success: boolean;
  message: string;
  generatedAt: string;
}> {
  const url = buildUrlWithParams(
    `/api/shops/${shopId}/dashboard-stats`,
    filters,
  );
  return apiRequest(url);
}

// -------------------------------------------
// Ganancia por método de pago
// -------------------------------------------
export async function getShopProfitByMethod(
  shopId: string,
  filters?: { startDate?: string; endDate?: string },
): Promise<{
  success: boolean;
  // ✅ CORREGIDO: Se restauró la clave 'data:'
  data: {
    efectivo: { totalVentas: number; ganancia: number; margen: number };
    transferencia: { totalVentas: number; ganancia: number; margen: number };
    total: { totalVentas: number; ganancia: number; margen: number };
  };
}> {
  const url = buildUrlWithParams(
    `/api/shops/${shopId}/sales/profit-by-method`,
    filters,
  );
  return apiRequest(url);
}

// -------------------------------------------
// Reporte de ventas (con paginación y exportación)
// -------------------------------------------
export async function getShopSalesReport(
  shopId: string,
  filters?: { startDate?: string; endDate?: string },
  page: number = 1,
  limit: number = 20,
  forExport: boolean = false,
): Promise<{
  // ✅ CORREGIDO: Estructura de 'data' restaurada correctamente
  data: { summary: any; data: any[]; total: number };
  success: boolean;
  message: string;
  generatedAt: string;
}> {
  const params: Record<string, any> = { ...filters };
  if (!forExport) {
    params.page = page;
    params.limit = limit;
  } else {
    params.export = true;
  }
  const url = buildUrlWithParams(`/api/shops/${shopId}/reports/sales`, params);
  return apiRequest(url);
}

// -------------------------------------------
// Reporte de productos (con paginación y exportación)
// -------------------------------------------
export async function getShopProductsReport(
  shopId: string,
  filters?: { startDate?: string; endDate?: string },
  page: number = 1,
  limit: number = 20,
  forExport: boolean = false,
): Promise<{
  // ✅ CORREGIDO: Estructura de 'data' restaurada correctamente
  data: { summary: any; data: any[]; total: number };
  success: boolean;
  message: string;
  generatedAt: string;
}> {
  const params: Record<string, any> = { ...filters };
  if (!forExport) {
    params.page = page;
    params.limit = limit;
  } else {
    params.export = true;
  }
  const url = buildUrlWithParams(
    `/api/shops/${shopId}/reports/products`,
    params,
  );
  return apiRequest(url);
}

// -------------------------------------------
// Reporte de clientes (con paginación y exportación)
// -------------------------------------------
export async function getShopCustomersReport(
  shopId: string,
  filters?: { startDate?: string; endDate?: string },
  page: number = 1,
  limit: number = 20,
  forExport: boolean = false,
): Promise<{
  // ✅ CORREGIDO: Estructura de 'data' restaurada correctamente
  data: { summary: any; data: any[] };
  success: boolean;
  message: string;
  generatedAt: string;
}> {
  const params: Record<string, any> = { ...filters };
  if (!forExport) {
    params.page = page;
    params.limit = limit;
  } else {
    params.export = true;
  }
  const url = buildUrlWithParams(
    `/api/shops/${shopId}/reports/customers`,
    params,
  );
  return apiRequest(url);
}

// -------------------------------------------
// Reporte de discrepancias (con paginación, búsqueda y exportación)
// -------------------------------------------
export async function getShopDiscrepancies(
  shopId: string,
  filters?: {
    startDate?: string;
    endDate?: string;
    search?: string;
    sortBy?: "date" | "amount";
    sortOrder?: "asc" | "desc";
  },
  page: number = 1,
  limit: number = 20,
  forExport: boolean = false,
): Promise<{
  // ✅ CORREGIDO: Estructura de 'data' restaurada correctamente
  data: { summary: any; data: any[] };
  success: boolean;
  message: string;
  generatedAt: string;
}> {
  const params: Record<string, any> = { ...filters };
  if (!forExport) {
    params.page = page;
    params.limit = limit;
  } else {
    params.export = true;
  }
  const url = buildUrlWithParams(
    `/api/shops/${shopId}/reports/discrepancies`,
    params,
  );
  return apiRequest(url);
}

// -------------------------------------------
// Reporte de transferencias (con vista, filtros y exportación)
// -------------------------------------------
export async function getShopTransfersReport(
  shopId: string,
  filters: {
    startDate?: string;
    endDate?: string;
    bank?: string;
    search?: string;
    page?: number;
    limit?: number;
    transferView?: string;
    forExport?: boolean;
  },
): Promise<any> {
  const params: Record<string, any> = {};
  if (filters.startDate) params.startDate = filters.startDate;
  if (filters.endDate) params.endDate = filters.endDate;
  if (filters.bank) params.bank = filters.bank;
  if (filters.search?.trim()) params.search = filters.search.trim();
  if (filters.transferView) params.transferView = filters.transferView;

  if (filters.forExport) {
    params.export = true;
  } else {
    if (filters.page) params.page = filters.page;
    if (filters.limit) params.limit = filters.limit;
  }

  const url = buildUrlWithParams(
    `/api/shops/${shopId}/sales/transfers/detailed`,
    params,
  );
  return apiRequest(url);
}

// -------------------------------------------
// Reporte consolidado para Owner
// -------------------------------------------
export async function getOwnerConsolidatedReport(
  ownerId: string,
  filters?: { startDate?: string; endDate?: string; shopIds?: string[] },
): Promise<{
  period: { start: string; end: string };
  summary: {
    totalRevenue: number;
    totalSales: number;
    averageTicket: number;
    activeShops: number;
  };
  shops: Array<{
    shopId: string;
    shopName: string;
    totalSales: number;
    totalRevenue: number;
    averageTicket: number;
    salesCount: number;
    paymentMethodBreakdown: { efectivo: number; transferencia: number };
  }>;
  hourlyHeatmap: Array<{
    hour: number;
    dayOfWeek: number;
    salesCount: number;
    revenue: number;
  }>;
  topProducts: Array<{
    productId: string;
    productName: string;
    category: string;
    totalSold: number;
    totalRevenue: number;
    shopsSelling: number;
  }>;
  lowStockAlerts: Array<{
    shopId: string;
    shopName: string;
    productName: string;
    currentStock: number;
    threshold: number;
  }>;
}> {
  const params: any = {};
  if (filters?.startDate) params.startDate = filters.startDate;
  if (filters?.endDate) params.endDate = filters.endDate;
  if (filters?.shopIds?.length) params.shopIds = filters.shopIds.join(",");

  const url = buildUrlWithParams(
    `/api/owners/${ownerId}/reports/consolidated`,
    params,
  );
  const response = await apiRequest<{ data: any }>(url);
  return response.data;
}

// -------------------------------------------
// Estadísticas en tiempo real para Owner
// -------------------------------------------
export async function getOwnerRealtimeStats(ownerId: string): Promise<{
  // ✅ CORREGIDO: Se restauró la clave 'data:'
  data: {
    summary: {
      totalRevenueToday: number;
      totalSalesToday: number;
      averageTicketToday: number;
      revenueYesterday: number;
      percentChange: number;
    };
    topShops: Array<{
      shopId: string;
      shopName: string;
      revenueToday: number;
      salesCountToday: number;
    }>;
    topProductsToday: Array<{
      productId: string;
      productName: string;
      category: string;
      totalSold: number;
      totalRevenue: number;
    }>;
    recentSales: Array<{
      saleId: string;
      shopId: string;
      shopName: string;
      total: number;
      paymentMethod: string;
      createdAt: string;
      customerName?: string;
    }>;
    hourlyTrend: Array<{ hour: number; salesCount: number; revenue: number }>;
    alerts: { lowStockCount: number; noSalesTodayCount: number };
  };
  lastUpdated: string;
  updateInterval: number;
}> {
  return apiRequest(`/api/owners/${ownerId}/realtime`);
}
